/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { gql, useQuery } from '@apollo/client';

import { getIsBot } from '../../../../../state/ducks/App/App-Selectors';
import { getBrandName } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { GRAPHQL_ENV } from '../../../../gql';
import SearchChipsUI from '../ElasticSearchSuggestions/SearchChipsUI';
import useRecentlySearched from '../../../../helpers/useRecentlySearched';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { emitCustomTrackingEvent } from '../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';
import { getCurrentPageType } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';

const useStyles = makeStyles(() => ({
    base: {
        padding: '16px',
        width: '100%',
    },
    title: {
        fontSize: '14px',
        fontWeight: 700,
        marginBottom: '16px',
    },
    skeletonImageRoot: {
        width: '100%',
        paddingTop: '100%',
        position: 'relative',
    },
    skeletonImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        margin: 0,
        borderRadius: '4px',
    },
    imageLink: {
        display: 'block',
        width: '100%',
    },
    imageRoot: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
    },
    image: {
        width: '100%',
        objectFit: 'cover',
        height: 'auto',
    },
}));

const FIND_TRENDING_SEARCHES = gql`
    query FindTrendingSearches($brand: String!, $environment: String!) {
        findTrendingSearches(brand: $brand, environment: $environment) {
            trendingSearches {
                phrase
                rank
            }
        }
    }`;

function SearchTypeAheadPopularSearches({
    setSearchValue, concatSearchTerm, cleanTerm, setShowSuggestions, setShowTypeAheadSuggestions, handleMobileClose, trackExperiment,
}) {
    const classes = useStyles();
    const isBot = useSelector(getIsBot);
    const brand = useSelector(getBrandName);
    const { addRecentlySearched } = useRecentlySearched();
    const dispatch = useDispatch();

    const pageType = useSelector(getCurrentPageType);

    if (isBot) {
        return null;
    }

    const {
        loading, error, data,
    } = useQuery(FIND_TRENDING_SEARCHES, {
        variables: {
            brand,
            environment: GRAPHQL_ENV,
        },
        apolloOptions: {
            fetchPolicy: 'cache-first',
        },
    });

    if (loading) {
        return <></>;
    }

    if (error) {
        mbpLogger.logError({
            function: 'SearchTypeAheadPopularSearches.js',
            message: 'Error loading data for trending searches',
            appName: process.env.npm_package_name,
            module: 'SearchTypeAheadPopularSearches',
            error,
        });
        return null;
    }

    if (!data?.findTrendingSearches?.trendingSearches || data?.findTrendingSearches?.trendingSearches?.length <= 0) {
        return null;
    }

    const chipDetails = [];

    data?.findTrendingSearches?.trendingSearches?.slice(0, 5)
        ?.forEach(({ phrase }) => {
            const details = {};
            const searchValue = phrase.toLowerCase();
            details.name = phrase;
            details.inputString = searchValue;
            details.link = `/searchterm/${concatSearchTerm(cleanTerm(searchValue)?.replace(/\s+/g, '+'), phrase)}`; // replacing spaces with +
            details.onClick = () => {
                setSearchValue(searchValue);
                addRecentlySearched({ searchTerm: phrase, value: cleanTerm(searchValue) });
                dispatch(trackEvent({
                    eventCategory: 'Site Search',
                    eventAction: searchValue,
                    eventLabel: 'TRENDING SEARCH | <<pageType>>',
                    searchVariety: 'Trending search',
                    searchPhrase: searchValue,
                    eventName: 'search',

                    search_type: 'trending_search',
                    search_term: searchValue?.toLowerCase?.(),
                    unique_search_term: searchValue,
                }));
                setShowSuggestions(false);
                setShowTypeAheadSuggestions(false);
                handleMobileClose();
                dispatch(emitCustomTrackingEvent({
                    eventType: 'custom',
                    eventSubType: 'feature-tracking',
                    action: 'Click',
                    category: 'Search',
                    label: 'trending',
                }));
                trackExperiment('trending', 'trending searches');
            };
            chipDetails.push(details);
        });

    return chipDetails?.length > 0
        ? (
            <div className={classes.base}>
                <div className={classes.title}>
                    Trending
                </div>
                <SearchChipsUI
                    chipDetails={chipDetails}
                    id="trending-searches"
                    searchStyle="V3"
                />
            </div>
        )
        : <></>;
}

SearchTypeAheadPopularSearches.propTypes = {
    setSearchValue: PropTypes.func.isRequired,
    setShowSuggestions: PropTypes.func.isRequired,
    setShowTypeAheadSuggestions: PropTypes.func.isRequired,
    handleMobileClose: PropTypes.func.isRequired,
    concatSearchTerm: PropTypes.func.isRequired,
    cleanTerm: PropTypes.func.isRequired,
    trackExperiment: PropTypes.func,
};

SearchTypeAheadPopularSearches.defaultProps = {
    trackExperiment: () => {},
};

export default React.memo(SearchTypeAheadPopularSearches);
